// src/components/profile-detail/ZodiacTab.jsx
import React from "react";
import {
  Grid, Box, Typography, Chip, Stack, Paper, Avatar, useTheme, useMediaQuery,
} from "@mui/material";
import EmojiSymbolsIcon from '@mui/icons-material/EmojiSymbols';
import StarIcon from '@mui/icons-material/Star';
import PsychologyIcon from '@mui/icons-material/Psychology';
import FavoriteIcon from '@mui/icons-material/Favorite';
import WorkIcon from '@mui/icons-material/Work';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import SchoolIcon from '@mui/icons-material/School';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
//import PersonIcon from '@mui/icons-material/Person';
import DiamondIcon from '@mui/icons-material/Diamond';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import SpaIcon from '@mui/icons-material/Spa';
import PublicIcon from '@mui/icons-material/Public';
import InfoIcon from '@mui/icons-material/Info';
import InfoCard from "./components/InfoCard";
import {
  getZodiacSign,
  getZodiacColor,
  getZodiacCompatibility,
  getZodiacInfo
} from "../../../../utils/zodiacUtils";

const ZodiacTab = ({ profile }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Get zodiac sign info from date of birth
  const zodiacInfo = profile?.date_of_birth ? getZodiacSign(profile.date_of_birth) : getZodiacInfo('unknown');

  // Get compatibility data
  const compatibility = getZodiacCompatibility(zodiacInfo.name);

  // Combine all zodiac info
  const fullZodiacInfo = {
    ...zodiacInfo,
    bestMatches: compatibility.best,
    avoid: compatibility.avoid,
    compatibility: [...compatibility.best, ...compatibility.good]
  };

  // Get zodiac color based on element
  const zodiacElementColor = getZodiacColor(fullZodiacInfo.element);

  // Helper function to get element icon
  const getElementIcon = (element) => {
    switch (element?.toLowerCase()) {
      case "fire": return <WhatshotIcon />;
      case "earth": return <SpaIcon />;
      case "air": return <PublicIcon />;
      case "water": return <SpaIcon />;
      default: return <SpaIcon />;
    }
  };

  // Render Info Item with icon
  const renderInfoItem = (icon, label, value) => (
    <Stack direction="row" spacing={2} alignItems="center" sx={{
      p: 2, borderRadius: 2, bgcolor: 'primary.alpha10',
      border: 1, borderColor: 'divider', mb: 1.5
    }}>
      <Box sx={{
        width: 40, height: 40, borderRadius: 2,
        bgcolor: 'primary.alpha20',
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        {React.cloneElement(icon, { sx: { color: 'primary.light' } })}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>{label}</Typography>
        <Typography variant="body1" color="text.primary" sx={{ fontWeight: 600 }}>{value || '-'}</Typography>
      </Box>
    </Stack>
  );

  return (
    <Grid container spacing={isMobile ? 2 : 3}>
      {/* Disclaimer - Styled like other info cards */}
      <Grid item xs={12}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: 'primary.alpha10',
            border: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 1
          }}
        >
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: 'primary.alpha20',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <InfoIcon sx={{ color: 'primary.light' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontWeight: 500,
                display: 'block',
                mb: 0.5
              }}
            >
              Disclaimer
            </Typography>
            <Typography
              variant="body2"
              color="text.primary"
              sx={{
                fontWeight: 500,
                lineHeight: 1.6
              }}
            >
              “Zodiac-related information provided on this platform is generated through computerized systems and is intended for general guidance only. It should not be considered absolute or definitive. As it is based on analytical and interpretative models, individual personalities and life circumstances may not fully correspond with these insights.”
            </Typography>
          </Box>
        </Paper>
      </Grid>

      {/* Main Zodiac Info Card */}
      <Grid item xs={12} md={4}>
        <InfoCard
          title="Zodiac Sign"
          icon={<StarIcon />}
          sx={{
            bgcolor: 'background.default',
            border: 1, borderColor: 'divider',
            height: '100%'
          }}
        >
          <Box sx={{ p: 1, pb: 0.5 }}>
            <Stack spacing={1.5}>
              {/* Zodiac Symbol with Chip and Circle */}
              <Paper elevation={0} sx={{
                p: 3, borderRadius: 2,
                bgcolor: 'primary.alpha10',
                border: 1, borderColor: 'divider',
                textAlign: 'center'
              }}>
                {/* Zodiac Symbol Circle with Element Color Background and White Symbol */}
                <Box
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    bgcolor: zodiacElementColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                    boxShadow: theme.shadows[3],
                    border: '2px solid',
                    borderColor: 'background.paper'
                  }}
                >
                  <Typography
                    variant="h2"
                    component="span"
                    sx={{
                      fontWeight: 700,
                      color: '#FFFFFF',
                      fontSize: '3.5rem',
                      lineHeight: 1,
                      fontFamily: '"Segoe UI Symbol", "Apple Symbols", "Noto Sans Symbols", "Symbola", "Arial Unicode MS", sans-serif'
                    }}
                  >
                    {fullZodiacInfo.symbol}
                  </Typography>
                </Box>

                <Typography variant="h4" color="text.primary" sx={{ fontWeight: 700, mb: 1 }}>
                  {fullZodiacInfo.name}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                  {fullZodiacInfo.dates}
                </Typography>

                {/* Element Chip */}
                <Chip
                  label={fullZodiacInfo.element}
                  icon={getElementIcon(fullZodiacInfo.element)}
                  sx={{
                    bgcolor: 'primary.alpha20',
                    color: 'primary.light',
                    fontWeight: 600,
                    border: 1,
                    borderColor: 'divider'
                  }}
                />
              </Paper>

              {/* Zodiac Details */}
              {renderInfoItem(<EmojiSymbolsIcon />, "Ruling Planet", fullZodiacInfo.planet)}
              {renderInfoItem(<DiamondIcon />, "Lucky Stone", fullZodiacInfo.stone)}

              {/* Lucky Numbers */}
              {fullZodiacInfo.luckyNumbers && fullZodiacInfo.luckyNumbers.length > 0 && (
                <Paper elevation={0} sx={{
                  p: 2, borderRadius: 2,
                  bgcolor: 'primary.alpha10',
                  border: 1, borderColor: 'divider'
                }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 1, display: 'block' }}>
                    Lucky Numbers
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {fullZodiacInfo.luckyNumbers.map((num, index) => (
                      <Box key={index} sx={{
                        width: 40, height: 40, borderRadius: '50%',
                        bgcolor: 'primary.alpha20',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: 1, borderColor: 'divider'
                      }}>
                        <Typography variant="h6" color="primary.light" sx={{ fontWeight: 800 }}>
                          {num}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              )}
            </Stack>
          </Box>
        </InfoCard>
      </Grid>

      {/* Personality Traits Card */}
      <Grid item xs={12} md={8}>
        <InfoCard
          title="Personality Traits"
          icon={<PsychologyIcon />}
          sx={{
            bgcolor: 'background.default',
            border: 1, borderColor: 'divider',
            height: '100%'
          }}
        >
          <Box sx={{ p: 1, pb: 0.5 }}>
            <Stack spacing={1.5}>
              {/* Description */}
              <Paper elevation={0} sx={{
                p: 3, borderRadius: 2,
                bgcolor: 'primary.alpha10',
                border: 1, borderColor: 'divider',
                fontStyle: 'italic'
              }}>
                <Typography variant="body1" color="text.primary" sx={{ lineHeight: 1.8 }}>
                  "{fullZodiacInfo.description}"
                </Typography>
              </Paper>

              {/* Strengths & Weaknesses */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Paper elevation={0} sx={{
                    p: 2, borderRadius: 2,
                    bgcolor: 'primary.alpha10',
                    border: 1, borderColor: 'divider',
                    height: '100%'
                  }}>
                    <Typography variant="subtitle1" color="success.main" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ color: 'success.main' }} /> Strengths
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {fullZodiacInfo.strengths && fullZodiacInfo.strengths.map((trait, index) => (
                        <Chip
                          key={index}
                          label={trait}
                          size="small"
                          sx={{
                            bgcolor: 'primary.alpha20',
                            color: 'primary.light',
                            fontWeight: 500,
                            border: 1,
                            borderColor: 'divider'
                          }}
                        />
                      ))}
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Paper elevation={0} sx={{
                    p: 2, borderRadius: 2,
                    bgcolor: 'primary.alpha10',
                    border: 1, borderColor: 'divider',
                    height: '100%'
                  }}>
                    <Typography variant="subtitle1" color="error.main" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CancelIcon sx={{ color: 'error.main' }} /> Weaknesses
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {fullZodiacInfo.weaknesses && fullZodiacInfo.weaknesses.map((trait, index) => (
                        <Chip
                          key={index}
                          label={trait}
                          size="small"
                          sx={{
                            bgcolor: 'primary.alpha20',
                            color: 'primary.light',
                            fontWeight: 500,
                            border: 1,
                            borderColor: 'divider'
                          }}
                        />
                      ))}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Stack>
          </Box>
        </InfoCard>
      </Grid>

      {/* Compatibility Card */}
      <Grid item xs={12}>
        <InfoCard
          title="Love & Compatibility"
          icon={<FavoriteIcon />}
          sx={{
            bgcolor: 'background.default',
            border: 1, borderColor: 'divider',
            minHeight: 280
          }}
        >
          <Box sx={{ p: 1, pb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Paper elevation={0} sx={{
                  p: 2, borderRadius: 2,
                  bgcolor: 'primary.alpha10',
                  border: 1, borderColor: 'divider',
                  height: '100%'
                }}>
                  <Typography variant="h6" color="success.main" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FavoriteIcon sx={{ color: 'success.main' }} /> Best Matches
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {fullZodiacInfo.bestMatches && fullZodiacInfo.bestMatches.map((match, index) => (
                      <Chip
                        key={index}
                        label={match}
                        icon={<CheckCircleIcon />}
                        size="small"
                        sx={{
                          bgcolor: 'primary.alpha20',
                          color: 'primary.light',
                          fontWeight: 500,
                          border: 1,
                          borderColor: 'divider'
                        }}
                      />
                    ))}
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper elevation={0} sx={{
                  p: 2, borderRadius: 2,
                  bgcolor: 'primary.alpha10',
                  border: 1, borderColor: 'divider',
                  height: '100%'
                }}>
                  <Typography variant="h6" color="error.main" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CancelIcon sx={{ color: 'error.main' }} /> Avoid Relationships
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {fullZodiacInfo.avoid && fullZodiacInfo.avoid.map((match, index) => (
                      <Chip
                        key={index}
                        label={match}
                        icon={<CancelIcon />}
                        size="small"
                        sx={{
                          bgcolor: 'primary.alpha20',
                          color: 'primary.light',
                          fontWeight: 500,
                          border: 1,
                          borderColor: 'divider'
                        }}
                      />
                    ))}
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper elevation={0} sx={{
                  p: 2, borderRadius: 2,
                  bgcolor: 'primary.alpha10',
                  border: 1, borderColor: 'divider',
                  height: '100%'
                }}>
                  <Typography variant="h6" color="primary.light" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FavoriteIcon sx={{ color: 'primary.light' }} /> Compatible Signs
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {fullZodiacInfo.compatibility && fullZodiacInfo.compatibility.map((sign, index) => (
                      <Chip
                        key={index}
                        label={sign}
                        size="small"
                        sx={{
                          bgcolor: 'primary.alpha20',
                          color: 'primary.light',
                          fontWeight: 500,
                          border: 1,
                          borderColor: 'divider'
                        }}
                      />
                    ))}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </InfoCard>
      </Grid>

      {/* Life Aspects Card */}
      <Grid item xs={12}>
        <InfoCard
          title="Life Aspects"
          icon={<TravelExploreIcon />}
          sx={{
            bgcolor: 'background.default',
            border: 1, borderColor: 'divider',
            minHeight: 300
          }}
        >
          <Box sx={{ p: 1, pb: 0.5 }}>
            <Grid container spacing={2}>
              {[
                { icon: <WorkIcon />, title: "Career & Money", content: fullZodiacInfo.career },
                { icon: <HealthAndSafetyIcon />, title: "Health", content: fullZodiacInfo.health },
                { icon: <FamilyRestroomIcon />, title: "Family & Relationships", content: fullZodiacInfo.family },
                { icon: <SchoolIcon />, title: "Personality", content: fullZodiacInfo.personality }
              ].map((aspect, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Paper elevation={0} sx={{
                      p: 3, borderRadius: 2,
                      bgcolor: 'primary.alpha10',
                      border: 1, borderColor: 'divider',
                      height: '100%', minHeight: '200px',
                      display: 'flex', flexDirection: 'column'
                    }}>
                      <Stack spacing={2} sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{
                            width: 50, height: 50, borderRadius: 2,
                            bgcolor: 'primary.alpha20',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }}>
                            {React.cloneElement(aspect.icon, { sx: { color: 'primary.light', fontSize: 28 } })}
                          </Box>
                          <Typography variant="h6" color="primary.light" sx={{ fontWeight: 600 }}>
                            {aspect.title}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2" color="text.secondary" sx={{
                            lineHeight: 1.7, flex: 1,
                            overflow: 'hidden', textOverflow: 'ellipsis'
                          }}>
                            {aspect.content || 'Information not available'}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </InfoCard>
      </Grid>

      {/* Famous People Card */}
      {fullZodiacInfo.famous && fullZodiacInfo.famous !== 'Unknown' && (
        <Grid item xs={12}>
          <InfoCard
            title="Famous People"
            icon={<StarIcon />}
            sx={{
              bgcolor: 'background.default',
              border: 1, borderColor: 'divider',
              minHeight: 150
            }}
          >
            <Box sx={{ p: 1, pb: 0.5 }}>
              <Paper elevation={0} sx={{
                p: 3, borderRadius: 2,
                bgcolor: 'primary.alpha10',
                border: 1, borderColor: 'divider'
              }}>
                <Typography variant="body1" color="text.primary" sx={{ lineHeight: 1.8, fontStyle: 'italic' }}>
                  "{fullZodiacInfo.famous}"
                </Typography>
              </Paper>
            </Box>
          </InfoCard>
        </Grid>
      )}

      {/* Summary Card */}
      <Grid item xs={12}>
        <Paper elevation={0} sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          bgcolor: 'primary.alpha10',
          border: 1, borderColor: 'divider',
          mt: 1
        }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'primary.alpha20', color: 'primary.light', mr: 2 }}>
                  <StarIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={700} color="text.primary">
                    Zodiac Summary
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Based on birth date: {profile?.date_of_birth || 'Not provided'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{
                display: 'flex', flexWrap: 'wrap', gap: 1.5,
                justifyContent: { xs: 'center', md: 'flex-end' }
              }}>
                {[
                  { icon: getElementIcon(fullZodiacInfo.element), label: fullZodiacInfo.element },
                  { icon: <EmojiSymbolsIcon />, label: fullZodiacInfo.planet },
                  { icon: <DiamondIcon />, label: fullZodiacInfo.stone },
                  { icon: <FavoriteIcon />, label: fullZodiacInfo.bestMatches && fullZodiacInfo.bestMatches[0] ? `Best: ${fullZodiacInfo.bestMatches[0]}` : null },
                  { icon: <CheckCircleIcon />, label: fullZodiacInfo.luckyNumbers && fullZodiacInfo.luckyNumbers[0] ? `Lucky #${fullZodiacInfo.luckyNumbers[0]}` : null }
                ].filter(item => item.label).map((item, index) => (
                  <Chip
                    key={index}
                    label={item.label}
                    icon={item.icon}
                    size="small"
                    sx={{
                      bgcolor: 'primary.alpha20',
                      color: 'primary.light',
                      fontWeight: 600,
                      border: 1,
                      borderColor: 'divider'
                    }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default ZodiacTab;